import type { Variant } from '~/types'
import { gidToId } from './storefront'

function isVariant(variant: Variant | any): variant is Variant {
	return variant?.id?.includes('ProductVariant')
}

export function track(event: FBEvent, data: FBEventData | Variant) {
	try {
		if (isVariant(data)) {
			const params: FBEventData = {
				content_type: 'product_group',
				content_ids: [gidToId(data.id).toString()],
				value: Number.parseFloat(data.price.amount),
				currency: data.price.currencyCode
			}
			fbq('track', event, params)
		} else {
			const params: FBEventData = {
				content_type: 'product_group',
				...data
			}
			fbq('track', event, params)
		}
	} catch {
		// Don't care if analytics fail
	}
}

// eslint-disable-next-line no-shadow -- TODO: No clue why this happens. Linter bug?
export enum FBEvent {
	/**
	 * @brief When payment information is added in the checkout flow.
	 *	A person clicks on a save billing information button.
	 *	@params "content_category, content_ids, contents, currency, value
	 *	Optional.
	 */
	AddPaymentInfo = 'AddPaymentInfo',
	/**
	 * @brief When a product is added to the shopping cart.
	 * A person clicks on an add to cart button.
	 * @params "content_ids, content_name, content_type, contents, currency, value
	 * Optional.Required for Dynamic Ads: content_type and contents"
	 */
	AddToCart = 'AddToCart',
	/**
	 * @brief When a product is added to a wishlist.
	 * A person clicks on an add to wishlist button.
	 * @params "content_name, content_category, content_ids, contents, currency, value
	 * Optional.
	 */
	AddToWishlist = 'AddToWishlist',
	/**
	 * @brief When a registration form is completed.
	 * A person submits a completed subscription or signup form.
	 * @params "content_name, currency, status, value
	 * Optional.
	 */
	CompleteRegistration = 'CompleteRegistration',
	/**
	 * @brief When a person initiates contact with your business via telephone, SMS, email, chat, etc.
	 * A person submits a question about a product.
	 * @params Optional.
	 */
	Contact = 'Contact',
	/**
	 * @brief When a person customizes a product.
	 * A person selects the color of a t-shirt.
	 * @params Optional.
	 */
	CustomizeProduct = 'CustomizeProduct',
	/**
	 * @brief When a person donates funds to your organization or cause.
	 * A person adds a donation to the Humane Society to their cart.
	 * @params Optional.
	 */
	Donate = 'Donate',
	/**
	 * @brief When a person searches for a location of your store via a website or app, with an intention to visit the physical location.
	 * A person wants to find a specific product in a local store.
	 * @params Optional.
	 */
	FindLocation = 'FindLocation',
	/**
	 * @brief When a person enters the checkout flow prior to completing the checkout flow.
	 * A person clicks on a checkout button.
	 * @params "content_category, content_ids, contents, currency, num_items, value
	 * Optional.
	 */
	InitiateCheckout = 'InitiateCheckout',
	/**
	 * @brief When a sign up is completed.
	 * A person clicks on pricing.
	 * @params "content_category, content_name, currency, value
	 * Optional.
	 */
	Lead = 'Lead',
	/**
	 * @brief This is the default Pixel tracking page visits.
	 * A person lands on your website pages.
	 * @params Optional.
	 */
	PageView = 'PageView',
	/**
	 * @brief When a purchase is made or checkout flow is completed.
	 * A person has finished the purchase or checkout flow and lands on thank you or confirmation page.
	 * @params "content_ids, content_name, content_type, contents, currency, num_items, value
	 * Required: currency and value
	 * Required for Dynamic Ads: content_type and contents, or content_ids"
	 */
	Purchase = 'Purchase',
	/**
	 * @brief When a person books an appointment to visit one of your locations.
	 * A person selects a date and time for a dentist appointment.
	 * @params Optional.
	 */
	Schedule = 'Schedule',
	/**
	 * @brief When a search is made.
	 * A person searches for a product on your website.
	 * @params "content_category, content_ids, contents, currency, search_string, value
	 * Optional.
	 */
	Search = 'Search',
	/**
	 * @brief When a person starts a free trial of a product or service you offer.
	 * A person selects a free week of your game.
	 * @params "currency, predicted_ltv, value
	 * Optional.
	 */
	StartTrial = 'StartTrial',
	/**
	 * @brief When a person applies for a product, service, or program you offer.
	 * A person applies for a credit card, educational program, or job.
	 * @params Optional.
	 */
	SubmitApplication = 'SubmitApplication',
	/**
	 * @brief When a person applies to a start a paid subscription for a product or service you offer.
	 * A person subscribes to your streaming service.
	 * @params "currency, predicted_ltv, value
	 * Optional.
	 */
	Subscribe = 'Subscribe',
	/**
	 * @brief A visit to a web page you care about (for example, a product page or landing page).
	 * ViewContent tells you if someone visits a web page's URL, but not what they see or do on that page.
	 * A person lands on a product details page.
	 * @params "content_ids, content_category, content_name, content_type, contents, currency, value
	 * Optional. Required for Dynamic Ads: content_type and contents, or content_ids"
	 */
	ViewContent = 'ViewContent'
}

type GetThirdArg<T> = T extends (first: string, second: string, third: infer ThirdArg) => any ? ThirdArg : never

export type FBEventData =
	| GetThirdArg<facebook.Pixel.Event>
	// TODO: Remove this when inferrence supports overloaded functions
	| facebook.Pixel.ViewContentParameters
	| facebook.Pixel.ViewContentParameters
	| facebook.Pixel.SearchParameters
	| facebook.Pixel.AddToCartParameters
	| facebook.Pixel.AddToWishlistParameters
	| facebook.Pixel.InitiateCheckoutParameters
	| facebook.Pixel.AddPaymentInfoParameters
	| facebook.Pixel.PurchaseParameters
	| facebook.Pixel.LeadParameters
	| facebook.Pixel.CompleteRegistrationParameters
